import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cake, AlertTriangle, Frown, Crown, QrCode } from 'lucide-react';
import { checkinRequestsApi } from '../../api/checkinRequestsApi';
import { baseApi } from '../../api/baseClient';
import { useAuth } from '../../context/AuthContext';
import { isPro as checkIsPro } from '../../utils/planPermissions';
import ActionCard from './ActionCard';

// Acciones de hoy desde lealify-base: GET /analytics/dashboard → today
// (cumpleañeros, insatisfechos sin atender, clientes inactivos).
interface TodayData {
  birthdays: number;
  dissatisfied: number;
  inactive: number;
}
interface DashboardResponse {
  today: TodayData;
}

const TodayActionsPanel: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const hasProAccess = checkIsPro(currentUser?.subscription);

  const [today, setToday] = useState<TodayData | null>(null);
  const [loading, setLoading] = useState(true);
  // Visitas por QR (8-oct-2026): pendientes que vencen a las 24 h, o invitación a prender el QR. Va en
  // gratis y PRO (el QR es gratis). null = aún no se sabe.
  const [qr, setQr] = useState<{ pending: number; enabled: boolean } | null>(null);
  useEffect(() => {
    let alive = true;
    checkinRequestsApi.count().then((r) => { if (alive) setQr({ pending: Number(r.pending) || 0, enabled: !!r.enabled }); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const qrCard = !qr ? null : qr.enabled && qr.pending > 0 ? (
    <ActionCard key="qr" tone="blue" icon={<QrCode className="h-5 w-5" />}
      title={qr.pending === 1 ? '1 visita por QR espera tu confirmación' : `${qr.pending} visitas por QR esperan tu confirmación`}
      sub="Confírmalas antes de 24 h o tu cliente pierde esos puntos." cta="Confirmar" to="/dashboard/checkin-approvals" />
  ) : !qr.enabled ? (
    <ActionCard key="qr" tone="green" icon={<QrCode className="h-5 w-5" />}
      title="¿Sin tablet en el mostrador? Tus clientes pueden registrarse solos"
      sub="Activa tu QR gratis, imprime el cartel y pégalo." cta="Ver cómo" to="/dashboard/checkin-approvals" />
  ) : null;

  useEffect(() => {
    if (!currentUser?.businessId || !hasProAccess) { setLoading(false); return; }
    let cancelled = false;

    const load = async () => {
      try {
        const data = await baseApi.get<DashboardResponse>('/analytics/dashboard');
        if (!cancelled) setToday(data.today);
      } catch (e) {
        if (!cancelled) console.error('[TodayActions] dashboard:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [currentUser?.businessId, hasProAccess]);

  // FREE: una sola tarjeta-invitación discreta en lugar de las tarjetas con datos
  if (!hasProAccess) {
    return (
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-content-3 mb-2">Para hoy</h3>
        {qrCard && <div className="mb-2">{qrCard}</div>}
        <div className="flex items-center gap-3 border border-dashed border-line rounded-lg px-4 py-3 bg-surface">
          <Crown className="h-5 w-5 text-amber-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-content">Cumpleaños de la semana y clientes en riesgo</p>
            <p className="text-xs text-content-3 mt-0.5">Con PRO, aquí verás a quién felicitar y a quién recuperar cada día.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/dashboard/subscription')}
            className="flex-shrink-0 text-xs font-bold text-accent border border-line rounded-md px-3 py-1.5 bg-surface hover:bg-surface-2 transition-colors"
          >
            Conocer PRO
          </button>
        </div>
      </div>
    );
  }

  if (loading) return null;
  if (!today) return null;

  const cards: React.ReactNode[] = [];
  if (qrCard && qr?.enabled) cards.push(qrCard); // pendientes: arriba de todo, vencen

  if (today.birthdays > 0) {
    cards.push(
      <ActionCard
        key="bday"
        tone="pink"
        icon={<Cake className="h-5 w-5" />}
        title={today.birthdays === 1 ? '1 cliente cumple años esta semana' : `${today.birthdays} clientes cumplen años esta semana`}
        sub="Felicítalos a tiempo."
        cta="Ver cumpleaños"
        to="/dashboard/birthday-panel"
      />
    );
  }

  if (today.inactive > 0) {
    cards.push(
      <ActionCard
        key="risk"
        tone="amber"
        icon={<AlertTriangle className="h-5 w-5" />}
        title={today.inactive === 1 ? '1 cliente frecuente lleva semanas sin venir' : `${today.inactive} clientes frecuentes llevan semanas sin venir`}
        sub='Una campaña de "te extrañamos" suele recuperar varios.'
        cta="Ver clientes"
        to="/dashboard/inactive-clients?segment=risk"
      />
    );
  }

  if (today.dissatisfied > 0) {
    cards.push(
      <ActionCard
        key="fb"
        tone="orange"
        icon={<Frown className="h-5 w-5" />}
        title={today.dissatisfied === 1 ? '1 cliente dejó feedback negativo' : `${today.dissatisfied} clientes dejaron feedback negativo`}
        sub="Contáctalos antes de que no vuelvan (últimos 7 días)."
        cta="Ver detalles"
        to="/dashboard/unsatisfied-clients"
      />
    );
  }

  if (qrCard && !qr?.enabled) cards.push(qrCard); // invitación: al final
  // Sin pendientes → el home queda limpio, sin cajas vacías
  if (cards.length === 0) return null;

  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-wider text-content-3 mb-2">Para hoy</h3>
      <div className="space-y-2">{cards}</div>
    </div>
  );
};

export default TodayActionsPanel;
